// src/components/admin/PlatformAdminConsole.tsx
// ERUE Festival Suite — Full Platform Admin Console (Sprint D)
// Access: PLATFORM_ADMIN only

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Shield, Key, ClipboardList, Plus, Edit2, Trash2,
  CheckCircle, XCircle, RefreshCw, Crown, Eye, EyeOff,
  AlertTriangle, Search, ChevronDown, Newspaper, Image,
  Radio, Building2, Settings, Mail, CalendarCheck, ShoppingBag,
  Hotel, Users2, Globe, Link2, ListTodo, BarChart3,
  HeartPulse, Flame, LogOut, ToggleLeft, ToggleRight,
  Send, Megaphone, X, Save, Award, Download, ChevronRight
} from 'lucide-react';
import {
  Permission, SystemRole, RBACUser, CustomRole, AuditEntry,
  ALL_PERMISSIONS, PERMISSION_LABELS, ROLE_PERMISSIONS, ROLE_DESCRIPTIONS
} from '../../types/rbac';
import PaystackConfigPanel      from './PaystackConfigPanel';
import MetaDistributionPanel    from './MetaDistributionPanel';
import { GlobalOverridesPanel } from './GlobalOverridesPanel';
import { AdminAnalyticsDashboard }  from './AdminAnalyticsDashboard';
import LOCContentUploader       from './LOCContentUploader';
import DigrnitariesManager      from './DigrnitariesManager';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444', amber:'#f59e0b',
  green:'#22c55e', blue:'#3b82f6', purple:'#8b5cf6'
};

type Section =
  'overview' |
  'users' | 'sessions' |
  'roles' | 'permissions' | 'audit' |
  'news' | 'gallery' | 'stream' | 'palace' |
  'overrides' | 'paystack' |
  'contacts-inbox' | 'event-regs' | 'store-orders' | 'hotel-regs' |
  'loc' | 'dignitaries' | 'diaspora-admin' |
  'meta' | 'telegram' | 'workflow' |
  'analytics' | 'data-health' | 'purge' |
  'villages' | 'chiefs' | 'email-blast' | 'email-config' |
  'collaboration-rooms' | 'collaboration-access' |
  'financial-hub-admin' | 'budget-admin' | 'reports-admin' |
  'maintenance' | 'events-admin' | 'contestants-admin' | 'sponsors-admin' | 'raffle-admin' | 'heritage-admin';

interface NavItem { id: Section; label: string; icon: React.ReactNode }
interface NavGroup { label: string; icon: string; items: NavItem[] }

const NAV: NavGroup[] = [
  { label:'Overview', icon:'📊', items:[
    { id:'overview',     label:'Dashboard',        icon:<BarChart3 size={14}/> },
  ]},
  { label:'Users', icon:'👥', items:[
    { id:'users',        label:'User Accounts',    icon:<Users size={14}/> },
    { id:'sessions',     label:'Active Sessions',  icon:<LogOut size={14}/> },
  ]},
  { label:'Access Control', icon:'🔐', items:[
    { id:'roles',        label:'Roles',            icon:<Key size={14}/> },
    { id:'permissions',  label:'Permissions',      icon:<Shield size={14}/> },
    { id:'audit',        label:'Audit Log',        icon:<ClipboardList size={14}/> },
  ]},
  { label:'Content', icon:'📰', items:[
    { id:'news',         label:'News & Articles',  icon:<Newspaper size={14}/> },
    { id:'gallery',      label:'Gallery',          icon:<Image size={14}/> },
    { id:'stream',       label:'Stream Config',    icon:<Radio size={14}/> },
    { id:'palace',       label:'Palace Info',      icon:<Building2 size={14}/> },
  ]},
  { label:'Settings', icon:'⚙️', items:[
    { id:'overrides',    label:'Global Overrides', icon:<Globe size={14}/> },
    { id:'paystack',     label:'Paystack Config',  icon:<Key size={14}/> },
    { id:'email-config', label:'Email Config',      icon:<Mail size={14}/> },
    { id:'email-blast',  label:'Email Blast',       icon:<Megaphone size={14}/> },
  ]},
  { label:'Operations', icon:'📬', items:[
    { id:'contacts-inbox', label:'Contact Inbox',   icon:<Mail size={14}/> },
    { id:'event-regs',    label:'Event Registrations', icon:<CalendarCheck size={14}/> },
    { id:'store-orders',  label:'Store Orders',    icon:<ShoppingBag size={14}/> },
    { id:'hotel-regs',    label:'Hotel Registrations', icon:<Hotel size={14}/> },
  ]},
    { label:'Collaboration', icon:'🤝', items:[
      { id:'collaboration-rooms',  label:'Rooms & Meetings',  icon:<Users size={14}/> },
      { id:'collaboration-access', label:'Access Control',    icon:<Shield size={14}/> },
    ]},
    { label:'Accounting & Budget', icon:'💰', items:[
      { id:'financial-hub-admin',  label:'Financial Hub',     icon:<span style={{fontSize:12}}>💰</span> },
      { id:'budget-admin',         label:'Budget Manager',    icon:<span style={{fontSize:12}}>📊</span> },
      { id:'reports-admin',        label:'Reports',           icon:<span style={{fontSize:12}}>📈</span> },
    ]},
  { label:'Committees', icon:'🤝', items:[
    { id:'loc',           label:'LOC Uploader',    icon:<Users2 size={14}/> },
    { id:'dignitaries',   label:'Dignitaries',     icon:<Crown size={14}/> },
    { id:'diaspora-admin',label:'Diaspora Admin',  icon:<Globe size={14}/> },
  ]},
  { label:'Kingdom', icon:'👑', items:[
    { id:'villages',     label:'Villages Registry', icon:<Building2 size={14}/> },
    { id:'chiefs',       label:'Chieftaincy',        icon:<Crown size={14}/> },
    { id:'heritage-admin',label:'Heritage Gallery',   icon:<Image size={14}/> },
  ]},
  { label:'Integrations', icon:'🔗', items:[
    { id:'meta',          label:'Meta Distribution', icon:<Megaphone size={14}/> },
    { id:'telegram',      label:'Telegram Bindings', icon:<Send size={14}/> },
    { id:'workflow',      label:'Workflow Tasks',  icon:<ListTodo size={14}/> },
  ]},
    { label:'Maintenance', icon:'🧹', items:[
      { id:'maintenance',      label:'Data Cleanup',      icon:<Flame size={14}/> },
      { id:'events-admin',     label:'Events Manager',    icon:<CalendarCheck size={14}/> },
      { id:'contestants-admin',label:'Contestants',       icon:<Users size={14}/> },
      { id:'sponsors-admin',   label:'Sponsors',          icon:<Award size={14}/> },
      { id:'raffle-admin',     label:'Raffle Prizes',     icon:<span style={{fontSize:12}}>🎟️</span> },
    ]},
  { label:'System', icon:'🏥', items:[
    { id:'analytics',     label:'Analytics',       icon:<BarChart3 size={14}/> },
    { id:'data-health',   label:'Data Health',     icon:<HeartPulse size={14}/> },
    { id:'purge',         label:'Purge Mock Data', icon:<Flame size={14}/> },
  ]},
];

const SYSTEM_ROLES: SystemRole[] = [
  'PLATFORM_ADMIN','FESTIVAL_COORDINATOR','ACCOUNTANT',
  'SECRETARY','COMMUNITY_MANAGER','MEDIA_MANAGER',
  'RAFFLE_MANAGER','REGISTRAR','VIEWER'
];

interface Props { onClose: () => void; currentUserEmail: string; }

export default function PlatformAdminConsole({ onClose, currentUserEmail }: Props) {
  const [section,    setSection]    = useState<Section>('overview');
  const [collapsed,  setCollapsed]  = useState<Record<string,boolean>>({});
  const [msg,        setMsg]        = useState<{type:'ok'|'err';text:string}|null>(null);

  const flash = (type:'ok'|'err', text:string) => {
    setMsg({type,text});
    setTimeout(()=>setMsg(null), 4000);
  };
  const tok = () => localStorage.getItem('erue_auth_token') || '';
  const api = useCallback(async (url:string, opts:RequestInit={}) => {
    const r = await fetch(url, {
      ...opts,
      headers:{'Content-Type':'application/json', Authorization:`Bearer ${tok()}`, ...(opts.headers||{})}
    });
    return r.json();
  }, []);

  const toggleGroup = (label:string) =>
    setCollapsed(p => ({...p, [label]: !p[label]}));

  const [sseAlerts, setSseAlerts] = useState<{pending:number;unread:number}>({pending:0,unread:0});
  useEffect(() => {
    const t = localStorage.getItem('erue_auth_token') || '';
    if (!t) return;
    const es = new EventSource(`/api/admin/alerts/stream?token=${encodeURIComponent(t)}`);
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.pending !== undefined || d.unread !== undefined)
          setSseAlerts({ pending: d.pending || 0, unread: d.unread || 0 });
      } catch {}
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, []);

  // ── sidebar ───────────────────────────────────────────────────────────────
  const Sidebar = () => (
    <div style={{
      width:220, minWidth:220, background:C.surface, borderRight:`1px solid ${C.border}`,
      overflowY:'auto', display:'flex', flexDirection:'column', flexShrink:0
    }}>
      <div style={{padding:'20px 16px 12px', borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <Crown size={18} color={C.gold}/>
          <span style={{color:C.gold, fontWeight:700, fontSize:13}}>Admin Console</span>
        </div>
        <div style={{color:C.dim, fontSize:10, marginTop:4, wordBreak:'break-all'}}>{currentUserEmail}</div>
      </div>
      <div style={{flex:1, padding:'8px 0'}}>
        {NAV.map(g => (
          <div key={g.label}>
            <button onClick={()=>toggleGroup(g.label)} style={{
              width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'6px 16px', background:'transparent', border:'none', cursor:'pointer',
              color:C.muted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1
            }}>
              <span>{g.icon} {g.label}</span>
              <ChevronRight size={10} style={{transform: collapsed[g.label] ? 'none':'rotate(90deg)', transition:'transform 0.2s'}}/>
            </button>
            {!collapsed[g.label] && g.items.map(item => (
              <button key={item.id} onClick={()=>setSection(item.id)} style={{
                width:'100%', display:'flex', alignItems:'center', gap:8,
                padding:'7px 20px', background: section===item.id ? `${C.gold}18` : 'transparent',
                border:'none', borderLeft: section===item.id ? `2px solid ${C.gold}` : '2px solid transparent',
                cursor:'pointer', color: section===item.id ? C.gold : C.muted,
                fontSize:12, textAlign:'left', transition:'all 0.15s'
              }}>
                {item.icon}{item.label}
              {item.id==='workflow' && sseAlerts.pending>0 && (
                <span style={{marginLeft:'auto',background:C.amber,color:'#070D0A',borderRadius:10,padding:'1px 6px',fontSize:10,fontWeight:700}}>
                  {sseAlerts.pending}
                </span>
              )}
              {item.id==='contacts-inbox' && sseAlerts.unread>0 && (
                <span style={{marginLeft:'auto',background:C.red,color:'#fff',borderRadius:10,padding:'1px 6px',fontSize:10,fontWeight:700}}>
                  {sseAlerts.unread}
                </span>
              )}
              </button>
            ))}
          </div>
        ))}
      </div>
      <button onClick={onClose} style={{
        margin:12, padding:'8px 0', background:'transparent',
        border:`1px solid ${C.border}`, borderRadius:8, color:C.dim,
        cursor:'pointer', fontSize:12
      }}>✕ Close Console</button>
    </div>
  );

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9500, background:'rgba(0,0,0,0.92)',
      display:'flex', flexDirection:'column'
    }}>
      {/* header */}
      <div style={{
        background:C.surface, borderBottom:`1px solid ${C.border}`,
        padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between',
        flexShrink:0
      }}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div style={{
            width:36, height:36, borderRadius:10, background:`${C.gold}22`,
            display:'flex', alignItems:'center', justifyContent:'center'
          }}>
            <Crown size={18} color={C.gold}/>
          </div>
          <div>
            <div style={{color:C.text, fontWeight:700, fontSize:16}}>Platform Administration Console</div>
            <div style={{color:C.dim, fontSize:11}}>ERUE Festival Suite — Full Admin Access</div>
          </div>
        </div>
        {msg && (
          <div style={{
            display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8,
            background: msg.type==='ok' ? `${C.green}22` : `${C.red}22`,
            border:`1px solid ${msg.type==='ok' ? C.green : C.red}44`,
            color: msg.type==='ok' ? C.green : C.red, fontSize:13
          }}>
            {msg.type==='ok' ? <CheckCircle size={14}/> : <AlertTriangle size={14}/>}
            {msg.text}
          </div>
        )}
        <button onClick={onClose} style={{
          background:'transparent', border:`1px solid ${C.border}`,
          color:C.muted, cursor:'pointer', borderRadius:8, padding:'6px 14px', fontSize:13
        }}>✕ Close</button>
      </div>

      {/* body */}
      <div style={{flex:1, display:'flex', overflow:'hidden'}}>
        <Sidebar/>
        <div style={{flex:1, overflowY:'auto', padding:28, background:C.bg}}>
          {section==='overview'      && <OverviewPanel    api={api} flash={flash}/>}
          {section==='users'         && <UsersPanel       api={api} flash={flash} currentUserEmail={currentUserEmail}/>}
          {section==='sessions'      && <SessionsPanel    api={api} flash={flash}/>}
          {section==='roles'         && <RolesPanel       api={api} flash={flash}/>}
          {section==='permissions'   && <PermissionsPanel/>}
          {section==='audit'         && <AuditPanel       api={api}/>}
          {section==='news'          && <NewsPanel        api={api} flash={flash}/>}
          {section==='gallery'       && <GalleryPanel     api={api} flash={flash}/>}
          {section==='stream'        && <StreamPanel      api={api} flash={flash}/>}
          {section==='palace'        && <PalacePanel      api={api} flash={flash}/>}
          {section==='overrides'     && <GlobalOverridesPanel adminEmail={currentUserEmail}/>}
          {section==='paystack'      && <PaystackConfigPanel/>}
          {section==='contacts-inbox'&& <ContactsInboxPanel api={api} flash={flash}/>}
          {section==='event-regs'    && <EventRegsPanel   api={api} flash={flash}/>}
          {section==='store-orders'  && <StoreOrdersPanel api={api}/>}
          {section==='hotel-regs'    && <HotelRegsPanel   api={api} flash={flash}/>}
          {section==='loc'           && <LOCContentUploader/>}
          {section==='dignitaries'   && <DigrnitariesManager/>}
          {section==='diaspora-admin'&& <DiasporaAdminPanel api={api} flash={flash}/>}
          {section==='meta'          && (
            <div>
              <MetaHealthIndicator api={api}/>
              <MetaDistributionPanel committeeId="admin"/>
            </div>
          )}
          {section==='telegram'      && <TelegramPanel    api={api} flash={flash}/>}
          {section==='workflow'      && <WorkflowPanel    api={api} flash={flash}/>}
          {section==='analytics'     && <AdminAnalyticsDashboard/>}
          {section==='data-health'   && <DataHealthPanel  api={api}/>}
          {section==='purge'         && <PurgePanel       api={api} flash={flash}/>}
          {section==='villages'      && <VillagesPanel     api={api} flash={flash}/>}
          {section==='chiefs'        && <ChieftaincyPanel  api={api} flash={flash}/>}
          {section==='email-config'  && <EmailConfigPanel  api={api} flash={flash}/>}
          {section==='email-blast'   && <EmailBlastPanel   api={api} flash={flash}/>}
          {(section==='collaboration-rooms'||section==='collaboration-access') && (
            <div style={{color:C.text}}>
              <div style={{marginBottom:20}}>
                <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>🤝 Collaboration Admin</h2>
                <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Manage collaboration rooms, participant access, and meeting records.</p>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:20}}>
                {['Rooms & Meetings','Participant Access','Room Audit Logs','Invite Management','Message History','Mute Controls'].map((m,i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
                    <div style={{color:C.gold,fontWeight:600,fontSize:13}}>✓ {m}</div>
                    <div style={{color:C.muted,fontSize:11,marginTop:4}}>Operational</div>
                  </div>
                ))}
              </div>
              <div style={{padding:16,background:C.surface,borderRadius:8,border:`1px solid ${C.border}`}}>
                <p style={{color:C.gold,fontWeight:700,marginBottom:6,fontSize:13}}>Access Control</p>
                <p style={{color:C.muted,fontSize:12,lineHeight:1.7}}>Use Participate → Collaborate to create rooms and invite members. Platform Admins can access all rooms. API: /api/collab/rooms/:id/audit for full audit trails. Use /api/collab/rooms/:id/participants/:email/mute to mute participants.</p>
              </div>
            </div>
          )}
          {section==='financial-hub-admin' && (
            <div style={{color:C.text}}>
              <div style={{marginBottom:20}}>
                <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>💰 Financial Hub — Admin Overview</h2>
                <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Full double-entry accounting, journals, bank accounts, payroll, tax, and inventory. Access the full interface via Administration → Financial Hub.</p>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:12}}>
                {[
                  {m:'Chart of Accounts', api:'/api/finance/accounts'},
                  {m:'Journal Entries',   api:'/api/finance/journals'},
                  {m:'Bank Accounts',     api:'/api/finance/bank-accounts'},
                  {m:'Payroll Runs',      api:'/api/payroll/runs'},
                  {m:'Tax Schedules',     api:'/api/finance/tax'},
                  {m:'Fixed Assets',      api:'/api/finance/fixed-assets'},
                  {m:'Trial Balance',     api:'/api/finance/trial-balance'},
                  {m:'Income & Expenditure', api:'/api/finance/income-expenditure'},
                ].map(({m,api:ep},i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
                    <div style={{color:C.green,fontWeight:600,fontSize:13}}>✓ {m}</div>
                    <div style={{color:C.muted,fontFamily:'monospace',fontSize:10,marginTop:4}}>{ep}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {section==='budget-admin' && (
            <div style={{color:C.text}}>
              <div style={{marginBottom:20}}>
                <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>📊 Budget Manager</h2>
                <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Create, approve, and track festival budget plans. Full interface: Administration → Accounting.</p>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:12}}>
                {[
                  {m:'Budget Plans',      api:'/api/finance/budgets'},
                  {m:'Budget Lines',     api:'/api/finance/budgets/:id'},
                  {m:'Revenue Tracking', api:'/api/admin/budget/plans/:id/revenue'},
                  {m:'Actual vs Budget', api:'/api/admin/budget/plans/:id/actuals'},
                  {m:'Budget Approval',  api:'/api/finance/budgets/:id/action'},
                  {m:'Sync Actuals',     api:'/api/finance/budgets/:id/sync-actuals'},
                ].map(({m,api:ep},i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
                    <div style={{color:C.gold,fontWeight:600,fontSize:13}}>✓ {m}</div>
                    <div style={{color:C.muted,fontFamily:'monospace',fontSize:10,marginTop:4}}>{ep}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {section==='reports-admin' && (
            <div style={{color:C.text}}>
              <div style={{marginBottom:20}}>
                <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>📈 Reports Module</h2>
                <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Financial summaries, payroll reports, tax schedules, and operational analytics.</p>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
                {[
                  '/api/reports/financial-summary',
                  '/api/reports/payroll-summary',
                  '/api/reports/tax-schedule',
                  '/api/reports/operational',
                  '/api/finance/trial-balance',
                  '/api/finance/income-expenditure',
                  '/api/finance/dashboard',
                  '/api/admin/budget/stats',
                ].map((ep,i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12}}>
                    <div style={{color:C.green,fontFamily:'monospace',fontSize:11}}>{ep}</div>
                    <div style={{color:C.muted,fontSize:11,marginTop:4}}>GET — Available</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section==='maintenance' && (
            <MaintenancePanel api={api} flash={flash}/>
          )}
          {section==='events-admin' && (
            <EventsAdminPanel api={api} flash={flash}/>
          )}
          {section==='contestants-admin' && (
            <ContestantsAdminPanel api={api} flash={flash}/>
          )}
          {section==='sponsors-admin' && (
            <SponsorsAdminPanel api={api} flash={flash}/>
          )}
          {section==='raffle-admin' && (
            <RaffleAdminPanel api={api} flash={flash}/>
          )}
          {section==='heritage-admin' && (
            <HeritageAdminPanel api={api} flash={flash}/>
          )}
        </div>
      </div>
    </div>
  );
}

// ── shared helpers ────────────────────────────────────────────────────────────
const PanelTitle = ({title,sub}:{title:string;sub?:string}) => (
  <div style={{marginBottom:20}}>
    <h2 style={{color:C.text, fontWeight:700, fontSize:18, margin:0}}>{title}</h2>
    {sub && <p style={{color:C.muted, fontSize:13, margin:'4px 0 0'}}>{sub}</p>}
  </div>
);

const Btn = ({onClick,children,variant='gold',disabled=false}:{
  onClick?:()=>void; children:React.ReactNode;
  variant?:'gold'|'ghost'|'red'|'green'; disabled?:boolean
}) => {
  const styles: Record<string,React.CSSProperties> = {
    gold:  {background:C.gold,  color:'#070D0A', border:'none'},
    ghost: {background:'transparent', color:C.muted, border:`1px solid ${C.border}`},
    red:   {background:`${C.red}22`, color:C.red, border:`1px solid ${C.red}44`},
    green: {background:`${C.green}22`, color:C.green, border:`1px solid ${C.green}44`},
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles[variant], borderRadius:8, padding:'7px 14px',
      fontSize:12, fontWeight:600, cursor:disabled?'not-allowed':'pointer',
      opacity:disabled?0.5:1, display:'flex', alignItems:'center', gap:6
    }}>{children}</button>
  );
};

const Input = ({value,onChange,placeholder,type='text',style={}}:{
  value:string; onChange:(v:string)=>void;
  placeholder?:string; type?:string; style?:React.CSSProperties
}) => (
  <input value={value} onChange={e=>onChange(e.target.value)}
    placeholder={placeholder} type={type}
    style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:8,
      padding:'8px 12px', color:C.text, fontSize:13, outline:'none',
      width:'100%', boxSizing:'border-box', ...style
    }}/>
);

const Textarea = ({value,onChange,placeholder,rows=4}:{
  value:string; onChange:(v:string)=>void; placeholder?:string; rows?:number
}) => (
  <textarea value={value} onChange={e=>onChange(e.target.value)}
    placeholder={placeholder} rows={rows}
    style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:8,
      padding:'8px 12px', color:C.text, fontSize:13, outline:'none',
      width:'100%', boxSizing:'border-box', resize:'vertical', fontFamily:'inherit'
    }}/>
);

const Card = ({children,style={}}:{children:React.ReactNode;style?:React.CSSProperties}) => (
  <div style={{
    background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
    padding:16, ...style
  }}>{children}</div>
);

const StatCard = ({label,value,color=C.gold}:{label:string;value:string|number;color?:string}) => (
  <Card style={{textAlign:'center', padding:'20px 16px'}}>
    <div style={{color, fontSize:28, fontWeight:700}}>{value}</div>
    <div style={{color:C.muted, fontSize:12, marginTop:4}}>{label}</div>
  </Card>
);

const Empty = ({icon,text}:{icon:React.ReactNode;text:string}) => (
  <div style={{textAlign:'center', padding:'48px 0', color:C.dim}}>
    <div style={{opacity:0.3, marginBottom:8}}>{icon}</div>
    <div style={{fontSize:13}}>{text}</div>
  </div>
);

// ── OVERVIEW PANEL ────────────────────────────────────────────────────────────
function OverviewPanel({api,flash}:{api:any;flash:any}) {
  const [stats, setStats] = useState<any>(null);
  useEffect(()=>{ 
    Promise.all([
      api('/api/admin/users'),
      api('/api/admin/news'),
      api('/api/admin/gallery'),
      api('/api/admin/event-registrations'),
    ]).then(([u,n,g,e])=>{
      setStats({
        users: u.users?.length ?? 0,
        news:  n.articles?.length ?? 0,
        gallery: g.items?.length ?? 0,
        regs:  e.registrations?.length ?? 0,
      });
    }).catch(()=>{});
  },[api]);
  return (
    <div>
      <PanelTitle title="Platform Overview" sub="Live system stats for ERUE Festival Suite"/>
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16, marginBottom:28}}>
        <StatCard label="Registered Users"   value={stats?.users    ?? '—'}/>
        <StatCard label="News Articles"      value={stats?.news     ?? '—'} color={C.blue}/>
        <StatCard label="Gallery Items"      value={stats?.gallery  ?? '—'} color={C.purple}/>
        <StatCard label="Event Registrations"value={stats?.regs     ?? '—'} color={C.green}/>
      </div>
      <Card>
        <div style={{color:C.gold, fontWeight:700, marginBottom:12, fontSize:14}}>Quick Actions</div>
        <div style={{display:'flex', flexWrap:'wrap', gap:10}}>
          {[
            ['📰 Add News',       'news'],
            ['🖼 Upload Gallery',  'gallery'],
            ['👤 Add User',        'users'],
            ['📡 Stream Config',   'stream'],
            ['🌐 Global Settings', 'overrides'],
            ['📊 Analytics',       'analytics'],
          ].map(([label])=>(
            <div key={label} style={{
              padding:'8px 14px', background:`${C.gold}11`, border:`1px solid ${C.gold}33`,
              borderRadius:8, color:C.gold, fontSize:12, cursor:'pointer'
            }}>{label}</div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── USERS PANEL ───────────────────────────────────────────────────────────────
function UsersPanel({api,flash,currentUserEmail}:{api:any;flash:any;currentUserEmail:string}) {
  const [users,   setUsers]   = useState<RBACUser[]>([]);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(false);
  const [form,    setForm]    = useState(false);
  const [editU,   setEditU]   = useState<RBACUser|null>(null);
  const [uName,   setUName]   = useState('');
  const [uEmail,  setUEmail]  = useState('');
  const [uPw,     setUPw]     = useState('');
  const [uRole,   setURole]   = useState<SystemRole>('VIEWER');
  const [showPw,  setShowPw]  = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    try { const d=await api('/api/admin/users'); if(d.success) setUsers(d.users||[]); }
    finally { setLoading(false); }
  },[api]);

  useEffect(()=>{ load(); },[load]);

  const openNew = () => {
    setEditU(null); setUName(''); setUEmail(''); setUPw(''); setURole('VIEWER'); setForm(true);
  };
  const openEdit = (u:RBACUser) => {
    setEditU(u); setUName(u.name); setUEmail(u.email); setUPw(''); setURole(u.role as SystemRole); setForm(true);
  };
  const save = async () => {
    if(!uName.trim()||!uEmail.trim()) return flash('err','Name and email required');
    if(!editU&&!uPw.trim()) return flash('err','Password required for new user');
    const body:any = {name:uName,email:uEmail,role:uRole};
    if(uPw) body.password = uPw;
    const url  = editU ? `/api/admin/users/${editU.id}` : '/api/admin/users';
    const meth = editU ? 'PUT' : 'POST';
    const d = await api(url,{method:meth,body:JSON.stringify(body)});
    if(d.success){ flash('ok', editU?'User updated':'User created'); setForm(false); load(); }
    else flash('err', d.error||'Failed');
  };
  const del = async(u:RBACUser)=>{
    if(u.email===currentUserEmail) return flash('err','Cannot delete your own account');
    if(!confirm(`Delete ${u.name}?`)) return;
    const d = await api(`/api/admin/users/${u.id}`,{method:'DELETE'});
    if(d.success){ flash('ok','Deleted'); load(); } else flash('err',d.error||'Failed');
  };
  const toggleActive = async(u:RBACUser)=>{
    await api(`/api/admin/users/${u.id}/toggle-active`,{method:'POST'});
    load();
  };
  const resetPw = async(u:RBACUser)=>{
    const pw = prompt('New password for '+u.name+':');
    if(!pw) return;
    const d = await api(`/api/admin/users/${u.id}/reset-password`,{method:'POST',body:JSON.stringify({password:pw})});
    if(d.success) flash('ok','Password reset'); else flash('err',d.error||'Failed');
  };

  const filtered = users.filter(u=>
    u.name.toLowerCase().includes(search.toLowerCase())||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PanelTitle title="User Accounts" sub={`${users.length} registered users`}/>
      <div style={{display:'flex', gap:12, marginBottom:16, alignItems:'center'}}>
        <div style={{position:'relative', flex:1, maxWidth:320}}>
          <Search size={13} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.dim}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search users…"
            style={{
              background:C.card, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'8px 12px 8px 32px', color:C.text, fontSize:13, outline:'none', width:'100%'
            }}/>
        </div>
        <Btn onClick={openNew}><Plus size={13}/>Add User</Btn>
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/></Btn>
      </div>

      {loading ? <div style={{textAlign:'center',padding:40,color:C.dim}}><RefreshCw size={20} className="animate-spin" style={{margin:'0 auto 8px',display:'block'}}/></div> :
      filtered.length===0 ? <Empty icon={<Users size={32}/>} text="No users found"/> :
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {filtered.map(u=>(
          <Card key={u.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px'}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{
                width:36,height:36,borderRadius:'50%',background:`${C.gold}22`,
                display:'flex',alignItems:'center',justifyContent:'center',
                color:C.gold,fontWeight:700,fontSize:14,flexShrink:0
              }}>{u.name.charAt(0).toUpperCase()}</div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{color:C.text,fontWeight:600,fontSize:13}}>{u.name}</span>
                  {!u.active && <span style={{color:C.red,fontSize:10,background:`${C.red}22`,padding:'1px 6px',borderRadius:4}}>inactive</span>}
                  {u.email===currentUserEmail && <Crown size={12} color={C.gold}/>}
                </div>
                <span style={{color:C.dim,fontSize:11}}>{u.email}</span>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{
                fontSize:10,padding:'3px 8px',borderRadius:20,
                background:`${C.gold}18`,color:C.gold,border:`1px solid ${C.gold}33`
              }}>{u.role}</span>
              {u.email!==currentUserEmail && (<>
                <Btn onClick={()=>openEdit(u)} variant="ghost"><Edit2 size={12}/></Btn>
                <Btn onClick={()=>toggleActive(u)} variant="ghost">
                  {u.active ? <ToggleRight size={12}/> : <ToggleLeft size={12}/>}
                </Btn>
                <Btn onClick={()=>resetPw(u)} variant="ghost"><Key size={12}/></Btn>
                <Btn onClick={()=>del(u)} variant="red"><Trash2 size={12}/></Btn>
              </>)}
            </div>
          </Card>
        ))}
      </div>}

      {form && (
        <div style={{position:'fixed',inset:0,zIndex:9600,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <Card style={{width:'100%',maxWidth:440}}>
            <div style={{color:C.text,fontWeight:700,fontSize:16,marginBottom:16}}>{editU?'Edit User':'Add User'}</div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Full Name</div><Input value={uName} onChange={setUName} placeholder="Full name"/></div>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Email</div><Input value={uEmail} onChange={setUEmail} placeholder="email@example.com" type="email"/></div>
              <div>
                <div style={{color:C.muted,fontSize:11,marginBottom:4}}>{editU?'New Password (blank = keep)':'Password'}</div>
                <div style={{position:'relative'}}>
                  <Input value={uPw} onChange={setUPw} type={showPw?'text':'password'} placeholder="••••••••"/>
                  <button onClick={()=>setShowPw(!showPw)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:C.dim,cursor:'pointer'}}>
                    {showPw?<EyeOff size={14}/>:<Eye size={14}/>}
                  </button>
                </div>
              </div>
              <div>
                <div style={{color:C.muted,fontSize:11,marginBottom:4}}>System Role</div>
                <select value={uRole} onChange={e=>setURole(e.target.value as SystemRole)} style={{
                  background:C.card,border:`1px solid ${C.border}`,borderRadius:8,
                  padding:'8px 12px',color:C.text,fontSize:13,outline:'none',width:'100%'
                }}>
                  {SYSTEM_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:16}}>
              <Btn onClick={()=>setForm(false)} variant="ghost">Cancel</Btn>
              <Btn onClick={save}><Save size={13}/>{editU?'Update':'Create'}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── SESSIONS PANEL ────────────────────────────────────────────────────────────
function SessionsPanel({api,flash}:{api:any;flash:any}) {
  const [sessions,setS]=useState<any[]>([]);
  const load=useCallback(async()=>{
    const d=await api('/api/admin/sessions'); if(d.success) setS(d.sessions||[]);
  },[api]);
  useEffect(()=>{load();},[load]);
  const revoke=async(id:string)=>{
    const d=await api(`/api/admin/sessions/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Session revoked');load();}else flash('err','Failed');
  };
  return (
    <div>
      <PanelTitle title="Active Sessions" sub="Manage user login sessions"/>
      <Btn onClick={load} variant="ghost" ><RefreshCw size={13}/>Refresh</Btn>
      <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
        {sessions.length===0 ? <Empty icon={<LogOut size={28}/>} text="No active sessions"/>:
        sessions.map((s:any)=>(
          <Card key={s.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{s.email||s.userId}</div>
              <div style={{color:C.dim,fontSize:11}}>{s.createdAt ? new Date(s.createdAt).toLocaleString():''}</div>
            </div>
            <Btn onClick={()=>revoke(s.id)} variant="red"><X size={12}/>Revoke</Btn>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── ROLES PANEL ───────────────────────────────────────────────────────────────
function RolesPanel({api,flash}:{api:any;flash:any}) {
  const [roles,setR]=useState<CustomRole[]>([]);
  const [form,setForm]=useState(false);
  const [rName,setRName]=useState('');
  const [rDesc,setRDesc]=useState('');
  const [rPerms,setRPerms]=useState<Permission[]>([]);
  const load=useCallback(async()=>{const d=await api('/api/admin/custom-roles');if(d.success)setR(d.roles||[]);},[api]);
  useEffect(()=>{load();},[load]);
  const toggleP=(p:Permission)=>setRPerms(prev=>prev.includes(p)?prev.filter(x=>x!==p):[...prev,p]);
  const save=async()=>{
    if(!rName.trim()) return flash('err','Name required');
    const d=await api('/api/admin/custom-roles',{method:'POST',body:JSON.stringify({name:rName,description:rDesc,permissions:rPerms})});
    if(d.success){flash('ok','Role created');setForm(false);setRName('');setRDesc('');setRPerms([]);load();}
    else flash('err',d.error||'Failed');
  };
  return (
    <div>
      <PanelTitle title="Roles & Permissions" sub="System roles and custom role definitions"/>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <Btn onClick={()=>setForm(true)}><Plus size={13}/>New Custom Role</Btn>
      </div>
      <div style={{color:C.gold,fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>System Roles (Built-in)</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10,marginBottom:24}}>
        {SYSTEM_ROLES.map(r=>(
          <Card key={r}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
              <span style={{color:C.gold,fontSize:12,fontWeight:700}}>{r}</span>
              <span style={{color:C.dim,fontSize:10}}>{(ROLE_PERMISSIONS[r]||[]).length} perms</span>
            </div>
            <p style={{color:C.muted,fontSize:11,margin:0}}>{ROLE_DESCRIPTIONS[r]}</p>
          </Card>
        ))}
      </div>
      {roles.length>0 && <>
        <div style={{color:C.gold,fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>Custom Roles</div>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {roles.map(r=>(
            <Card key={r.id}>
              <div style={{color:C.text,fontWeight:600,marginBottom:4}}>{r.name}</div>
              {r.description&&<p style={{color:C.muted,fontSize:11,margin:'0 0 8px'}}>{r.description}</p>}
              <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                {r.permissions.map(p=>(
                  <span key={p} style={{fontSize:10,padding:'2px 8px',background:`${C.green}18`,color:C.green,borderRadius:10,border:`1px solid ${C.green}33`}}>{PERMISSION_LABELS[p]}</span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </>}
      {form&&(
        <div style={{position:'fixed',inset:0,zIndex:9600,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <Card style={{width:'100%',maxWidth:520,maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{color:C.text,fontWeight:700,fontSize:16,marginBottom:14}}>Create Custom Role</div>
            <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:14}}>
              <Input value={rName} onChange={setRName} placeholder="Role name"/>
              <Input value={rDesc} onChange={setRDesc} placeholder="Description (optional)"/>
            </div>
            <div style={{color:C.muted,fontSize:11,marginBottom:8}}>Select permissions:</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:14}}>
              {ALL_PERMISSIONS.map(p=>(
                <label key={p} style={{
                  display:'flex',alignItems:'center',gap:8,padding:'8px 10px',
                  background:rPerms.includes(p)?`${C.gold}18`:C.surface,
                  border:`1px solid ${rPerms.includes(p)?C.gold:C.border}`,
                  borderRadius:8,cursor:'pointer'
                }}>
                  <input type="checkbox" checked={rPerms.includes(p)} onChange={()=>toggleP(p)} style={{accentColor:C.gold}}/>
                  <span style={{fontSize:11,color:C.text}}>{PERMISSION_LABELS[p]}</span>
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:10}}>
              <Btn onClick={()=>setForm(false)} variant="ghost">Cancel</Btn>
              <Btn onClick={save}>Create Role</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── PERMISSIONS PANEL ─────────────────────────────────────────────────────────
function PermissionsPanel() {
  return (
    <div>
      <PanelTitle title="System Permissions" sub="All permissions available in the platform"/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:10}}>
        {ALL_PERMISSIONS.map((p,i)=>(
          <Card key={p} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 14px'}}>
            <div style={{
              width:28,height:28,borderRadius:8,background:`${C.gold}18`,
              display:'flex',alignItems:'center',justifyContent:'center',
              color:C.gold,fontSize:11,fontWeight:700,flexShrink:0
            }}>{i+1}</div>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{PERMISSION_LABELS[p]}</div>
              <div style={{color:C.dim,fontSize:10,fontFamily:'monospace',marginTop:2}}>{p}</div>
            </div>
            <CheckCircle size={13} color={C.green} style={{flexShrink:0,marginTop:2}}/>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── AUDIT PANEL ───────────────────────────────────────────────────────────────
function AuditPanel({api}:{api:any}) {
  const [entries,setE]=useState<AuditEntry[]>([]);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true); try{ const d=await api('/api/admin/audit-log'); if(d.success) setE(d.logs||[]); }finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  return (
    <div>
      <PanelTitle title="Audit Log" sub="System activity trail"/>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      </div>
      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
      entries.length===0?<Empty icon={<ClipboardList size={28}/>} text="No audit entries yet"/>:
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {entries.slice(0,100).map(e=>(
          <Card key={e.id} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'10px 14px'}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:C.gold,flexShrink:0,marginTop:4}}/>
            <div style={{flex:1}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                <span style={{color:C.text,fontSize:12,fontWeight:600}}>{e.userEmail}</span>
                <span style={{color:C.dim,fontSize:12}}>→</span>
                <span style={{color:C.green,fontSize:12}}>{e.action}</span>
                 <span style={{color:C.muted,fontSize:12}}>{e.resource}{e.resourceId?' #'+e.resourceId:''}</span>
              </div>
              {e.details&&<div style={{color:C.dim,fontSize:11,marginTop:2}}>{typeof e.details==='string'?e.details:JSON.stringify(e.details)}</div>}
            </div>
            <span style={{color:C.dim,fontSize:10,flexShrink:0}}>{new Date(e.createdAt).toLocaleString()}</span>
          </Card>
        ))}
      </div>}
    </div>
  );
}

// ── NEWS PANEL ────────────────────────────────────────────────────────────────
function NewsPanel({api,flash}:{api:any;flash:any}) {
  const [articles,setA]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const [form,setForm]=useState(false);
  const [editN,setEditN]=useState<any>(null);
  const [title,setTitle]=useState('');
  const [excerpt,setExcerpt]=useState('');
  const [body,setBody]=useState('');
  const [category,setCategory]=useState('General');
  const [imageUrl,setImageUrl]=useState('');
  const [tags,setTags]=useState('');
  const [isBreaking,setIsBreaking]=useState(false);

  const load=useCallback(async()=>{
    setL(true);
    try{ const d=await api('/api/admin/news'); if(d.success) setA(d.articles||[]); }
    finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);

  const openNew=()=>{
    setEditN(null);setTitle('');setExcerpt('');setBody('');
    setCategory('General');setImageUrl('');setTags('');setIsBreaking(false);setForm(true);
  };
  const openEdit=(n:any)=>{
    setEditN(n);setTitle(n.title||'');setExcerpt(n.excerpt||'');setBody(n.body||n.content||'');
    setCategory(n.category||'General');setImageUrl(n.imageUrl||n.image||'');
    setTags((n.tags||[]).join(', '));setIsBreaking(n.isBreaking||false);setForm(true);
  };
  const save=async()=>{
    if(!title.trim()) return flash('err','Title required');
    const payload={
      title,excerpt,content:body,category,featuredImageUrl:imageUrl,isBreaking,
      tags:tags.split(',').map((t:string)=>t.trim()).filter(Boolean)
    };
    const url=editN?`/api/admin/news/${editN.id}`:'/api/admin/news';
    const method=editN?'PUT':'POST';
    const d=await api(url,{method,body:JSON.stringify(payload)});
    if(d.success){flash('ok',editN?'Article updated':'Article created');setForm(false);load();}
    else flash('err',d.error||'Failed');
  };
  const publish=async(n:any)=>{
    const d=await api(`/api/admin/news/${n.id}/publish`,{method:'PUT',body:JSON.stringify({published:!n.isPublished})});
    if(d.success){flash('ok',n.isPublished?'Unpublished':'Published');load();}
    else flash('err',d.error||'Failed');
  };
  const del=async(n:any)=>{
    if(!confirm(`Delete "${n.title}"?`)) return;
    const d=await api(`/api/admin/news/${n.id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err','Failed');
  };

  return (
    <div>
      <PanelTitle title="News & Articles" sub={`${articles.length} articles · manage festival news`}/>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16,gap:10}}>
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/></Btn>
        <Btn onClick={openNew}><Plus size={13}/>New Article</Btn>
      </div>

      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
      articles.length===0?<Empty icon={<Newspaper size={32}/>} text="No articles yet — create the first one"/>:
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {articles.map((n:any)=>(
          <Card key={n.id} style={{display:'flex',gap:14,alignItems:'flex-start'}}>
            {(n.imageUrl||n.image)&&(
              <img src={n.imageUrl||n.image} alt={n.title}
                style={{width:72,height:56,objectFit:'cover',borderRadius:8,flexShrink:0}}
                onError={e=>{(e.target as HTMLImageElement).style.display='none'}}/>
            )}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4}}>
                <span style={{color:C.text,fontWeight:600,fontSize:14}}>{n.title}</span>
                {n.isBreaking&&<span style={{fontSize:10,padding:'2px 6px',background:`${C.red}22`,color:C.red,borderRadius:4,border:`1px solid ${C.red}44`}}>BREAKING</span>}
                <span style={{
                  fontSize:10,padding:'2px 8px',borderRadius:10,
                  background: n.isPublished?`${C.green}22`:`${C.amber}22`,
                  color: n.isPublished?C.green:C.amber,
                  border:`1px solid ${n.isPublished?C.green:C.amber}44`
                }}>{n.isPublished?'Published':'Draft'}</span>
              </div>
              {n.excerpt&&<p style={{color:C.muted,fontSize:12,margin:'0 0 4px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{n.excerpt}</p>}
              <div style={{color:C.dim,fontSize:10}}>{n.category} · {n.createdAt?new Date(n.createdAt).toLocaleDateString():''}</div>
            </div>
            <div style={{display:'flex',gap:6,flexShrink:0}}>
              <Btn onClick={()=>publish(n)} variant={n.isPublished?'ghost':'green'}>
                {n.isPublished?<EyeOff size={12}/>:<Eye size={12}/>}
                {n.isPublished?'Unpublish':'Publish'}
              </Btn>
              <Btn onClick={()=>openEdit(n)} variant="ghost"><Edit2 size={12}/></Btn>
              <Btn onClick={()=>del(n)} variant="red"><Trash2 size={12}/></Btn>
            </div>
          </Card>
        ))}
      </div>}

      {form&&(
        <div style={{position:'fixed',inset:0,zIndex:9600,background:'rgba(0,0,0,0.8)',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:20,overflowY:'auto'}}>
          <Card style={{width:'100%',maxWidth:640,margin:'20px auto'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <div style={{color:C.text,fontWeight:700,fontSize:16}}>{editN?'Edit Article':'New Article'}</div>
              <button onClick={()=>setForm(false)} style={{background:'none',border:'none',color:C.dim,cursor:'pointer'}}><X size={18}/></button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Title *</div><Input value={title} onChange={setTitle} placeholder="Article title"/></div>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Excerpt / Summary</div><Input value={excerpt} onChange={setExcerpt} placeholder="Short summary shown in listings"/></div>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Body / Full Content *</div><Textarea value={body} onChange={setBody} placeholder="Full article content…" rows={6}/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <div style={{color:C.muted,fontSize:11,marginBottom:4}}>Category</div>
                  <select value={category} onChange={e=>setCategory(e.target.value)} style={{
                    background:C.card,border:`1px solid ${C.border}`,borderRadius:8,
                    padding:'8px 12px',color:C.text,fontSize:13,outline:'none',width:'100%'
                  }}>
                    {['General','Festival','Cultural','Royal','Diaspora','Security','Finance','Media'].map(c=>
                      <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Image URL</div><Input value={imageUrl} onChange={setImageUrl} placeholder="https://…"/></div>
              </div>
              <div><div style={{color:C.muted,fontSize:11,marginBottom:4}}>Tags (comma-separated)</div><Input value={tags} onChange={setTags} placeholder="festival, culture, royal…"/></div>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                <input type="checkbox" checked={isBreaking} onChange={e=>setIsBreaking(e.target.checked)} style={{accentColor:C.red,width:14,height:14}}/>
                <span style={{color:C.text,fontSize:13}}>Mark as Breaking News</span>
                {isBreaking&&<span style={{fontSize:10,padding:'2px 6px',background:`${C.red}22`,color:C.red,borderRadius:4}}>BREAKING</span>}
              </label>
            </div>
            <div style={{display:'flex',gap:10,marginTop:18}}>
              <Btn onClick={()=>setForm(false)} variant="ghost">Cancel</Btn>
              <Btn onClick={save}><Save size={13}/>{editN?'Update Article':'Save as Draft'}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── GALLERY PANEL ─────────────────────────────────────────────────────────────
function GalleryPanel({api,flash}:{api:any;flash:any}) {
  const [items,setI]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const [url,setUrl]=useState('');
  const [caption,setCaption]=useState('');
  const [adding,setAdding]=useState(false);

  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/admin/gallery');if(d.success)setI(d.items||d.gallery||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);

  const add=async()=>{
    if(!url.trim()) return flash('err','URL required');
    const d=await api('/api/admin/gallery',{method:'POST',body:JSON.stringify({url,caption})});
    if(d.success){flash('ok','Added');setUrl('');setCaption('');setAdding(false);load();}else flash('err',d.error||'Failed');
  };
  const del=async(id:string)=>{
    if(!confirm('Delete item?')) return;
    const d=await api(`/api/admin/gallery/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err','Failed');
  };

  return (
    <div>
      <PanelTitle title="Gallery" sub={`${items.length} gallery items`}/>
      <div style={{display:'flex',gap:10,marginBottom:16}}>
        <Btn onClick={()=>setAdding(!adding)}><Plus size={13}/>Add Item</Btn>
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/></Btn>
      </div>
      {adding&&(
        <Card style={{marginBottom:16}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:10}}>
            <Input value={url} onChange={setUrl} placeholder="Image URL https://…"/>
            <Input value={caption} onChange={setCaption} placeholder="Caption (optional)"/>
          </div>
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={()=>setAdding(false)} variant="ghost">Cancel</Btn>
            <Btn onClick={add}><Save size={13}/>Add to Gallery</Btn>
          </div>
        </Card>
      )}
      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
      items.length===0?<Empty icon={<Image size={32}/>} text="No gallery items yet"/>:
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12}}>
        {items.map((it:any)=>(
          <Card key={it.id} style={{padding:0,overflow:'hidden'}}>
            <img src={it.url||it.imageUrl} alt={it.caption||''}
              style={{width:'100%',height:120,objectFit:'cover',display:'block'}}
              onError={e=>{(e.target as HTMLImageElement).style.background=C.surface}}/>
            <div style={{padding:'8px 10px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{color:C.muted,fontSize:11,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{it.caption||'—'}</span>
              <button onClick={()=>del(it.id)} style={{background:'none',border:'none',color:C.red,cursor:'pointer',padding:2}}><Trash2 size={12}/></button>
            </div>
          </Card>
        ))}
      </div>}
    </div>
  );
}

// ── STREAM CONFIG PANEL ───────────────────────────────────────────────────────
function StreamPanel({api,flash}:{api:any;flash:any}) {
  const [cfg,setCfg]=useState<any>(null);
  const [youtubeUrl,setYT]=useState('');
  const [facebookUrl,setFB]=useState('');
  const [isLive,setLive]=useState(false);
  const [title,setTitle]=useState('');
  const [saving,setSaving]=useState(false);

  const load=useCallback(async()=>{
    const d=await api('/api/admin/stream-config');
    if(d.success||d.config){
      const c=d.config||d;
      setCfg(c);setYT(c.youtubeUrl||'');setFB(c.facebookUrl||'');
      setLive(c.isLive||false);setTitle(c.title||'');
    }
  },[api]);
  useEffect(()=>{load();},[load]);

  const save=async()=>{
    setSaving(true);
    try{
      const d=await api('/api/admin/stream-config',{method:'PUT',body:JSON.stringify({youtubeUrl,facebookUrl,isLive,title})});
      if(d.success) flash('ok','Stream config saved'); else flash('err',d.error||'Failed');
    }finally{setSaving(false);}
  };

  return (
    <div>
      <PanelTitle title="Live Stream Configuration" sub="Manage YouTube and Facebook live stream URLs"/>
      <Card style={{maxWidth:560}}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div>
            <div style={{color:C.muted,fontSize:11,marginBottom:4}}>Stream Title</div>
            <Input value={title} onChange={setTitle} placeholder="ERUE Festival 2026 Live Stream"/>
          </div>
          <div>
            <div style={{color:C.muted,fontSize:11,marginBottom:4}}>YouTube Live URL</div>
            <Input value={youtubeUrl} onChange={setYT} placeholder="https://youtube.com/live/…"/>
          </div>
          <div>
            <div style={{color:C.muted,fontSize:11,marginBottom:4}}>Facebook Live URL</div>
            <Input value={facebookUrl} onChange={setFB} placeholder="https://facebook.com/…/live"/>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
            <input type="checkbox" checked={isLive} onChange={e=>setLive(e.target.checked)} style={{accentColor:C.green,width:16,height:16}}/>
            <span style={{color:C.text,fontSize:13}}>Stream is currently LIVE</span>
            {isLive&&<span style={{fontSize:10,padding:'2px 8px',background:`${C.red}22`,color:C.red,borderRadius:4,border:`1px solid ${C.red}44`,animation:'pulse 2s infinite'}}>● LIVE</span>}
          </label>
          <Btn onClick={save} disabled={saving}><Save size={13}/>{saving?'Saving…':'Save Stream Config'}</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── PALACE INFO PANEL ─────────────────────────────────────────────────────────
function PalacePanel({api,flash}:{api:any;flash:any}) {
  const [data,setData]=useState<any>(null);
  const [saving,setSaving]=useState(false);
  const [json,setJson]=useState('');

  const load=useCallback(async()=>{
    const d=await api('/api/admin/palace');
    if(d.success||d.palace){const p=d.palace||d;setData(p);setJson(JSON.stringify(p,null,2));}
  },[api]);
  useEffect(()=>{load();},[load]);

  const save=async()=>{
    setSaving(true);
    try{
      let parsed;
      try{parsed=JSON.parse(json);}catch{return flash('err','Invalid JSON');}
      const d=await api('/api/admin/palace',{method:'PUT',body:JSON.stringify(parsed)});
      if(d.success) flash('ok','Palace info saved'); else flash('err',d.error||'Failed');
    }finally{setSaving(false);}
  };

  return (
    <div>
      <PanelTitle title="Palace Information" sub="Manage palace content and royal information"/>
      <Card>
        <div style={{color:C.muted,fontSize:11,marginBottom:8}}>Palace Data (JSON editor)</div>
        <Textarea value={json} onChange={setJson} rows={14}/>
        <div style={{display:'flex',gap:10,marginTop:12}}>
          <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Reload</Btn>
          <Btn onClick={save} disabled={saving}><Save size={13}/>{saving?'Saving…':'Save Palace Info'}</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── CONTACT INBOX PANEL ───────────────────────────────────────────────────────
function ContactsInboxPanel({api,flash}:{api:any;flash:any}) {
  const [msgs,setM]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/admin/contact-messages');if(d.success)setM(d.messages||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  const updateStatus=async(id:string,status:string)=>{
    const d=await api(`/api/admin/contact-messages/${id}/status`,{method:'PUT',body:JSON.stringify({status})});
    if(d.success){flash('ok','Status updated');load();}else flash('err','Failed');
  };
  return (
    <div>
      <PanelTitle title="Contact Message Inbox" sub={`${msgs.length} messages received`}/>
      <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:10}}>
        {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
        msgs.length===0?<Empty icon={<Mail size={28}/>} text="No contact messages"/>:
        msgs.map((m:any)=>(
          <Card key={m.id}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
                  <span style={{color:C.text,fontWeight:600,fontSize:13}}>{m.name||m.senderName}</span>
                  <span style={{color:C.dim,fontSize:11}}>{m.email||m.senderEmail}</span>
                  <span style={{
                    fontSize:10,padding:'2px 6px',borderRadius:4,
                    background: m.status==='read'?`${C.green}22`:`${C.amber}22`,
                    color: m.status==='read'?C.green:C.amber
                  }}>{m.status||'unread'}</span>
                </div>
                {m.subject&&<div style={{color:C.muted,fontSize:12,marginBottom:4,fontWeight:600}}>{m.subject}</div>}
                <div style={{color:C.muted,fontSize:12}}>{m.message||m.body}</div>
                <div style={{color:C.dim,fontSize:10,marginTop:6}}>{m.createdAt?new Date(m.createdAt).toLocaleString():''}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {m.status!=='read'&&<Btn onClick={()=>updateStatus(m.id,'read')} variant="green"><CheckCircle size={12}/>Mark Read</Btn>}
                {m.status!=='archived'&&<Btn onClick={()=>updateStatus(m.id,'archived')} variant="ghost"><X size={12}/>Archive</Btn>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── EVENT REGISTRATIONS PANEL ─────────────────────────────────────────────────
function EventRegsPanel({api,flash}:{api:any;flash:any}) {
  const [regs,setR]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/admin/event-registrations');if(d.success)setR(d.registrations||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  const updateStatus=async(id:string,status:string)=>{
    const d=await api(`/api/admin/event-registrations/${id}/status`,{method:'PUT',body:JSON.stringify({status})});
    if(d.success){flash('ok','Updated');load();}else flash('err','Failed');
  };
  return (
    <div>
      <PanelTitle title="Event Registrations" sub={`${regs.length} total registrations`}/>
      <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
        {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
        regs.length===0?<Empty icon={<CalendarCheck size={28}/>} text="No event registrations"/>:
        regs.map((r:any)=>(
          <Card key={r.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{r.name||r.fullName}</div>
              <div style={{color:C.dim,fontSize:11}}>{r.email} · {r.eventType||r.type} · {r.createdAt?new Date(r.createdAt).toLocaleDateString():''}</div>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{
                fontSize:10,padding:'2px 8px',borderRadius:10,
                background: r.status==='approved'?`${C.green}22`:r.status==='rejected'?`${C.red}22`:`${C.amber}22`,
                color: r.status==='approved'?C.green:r.status==='rejected'?C.red:C.amber
              }}>{r.status||'pending'}</span>
              {r.status!=='approved'&&<Btn onClick={()=>updateStatus(r.id,'approved')} variant="green"><CheckCircle size={12}/></Btn>}
              {r.status!=='rejected'&&<Btn onClick={()=>updateStatus(r.id,'rejected')} variant="red"><XCircle size={12}/></Btn>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── STORE ORDERS PANEL ────────────────────────────────────────────────────────
function StoreOrdersPanel({api}:{api:any}) {
  const [orders,setO]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/admin/store/orders');if(d.success)setO(d.orders||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  return (
    <div>
      <PanelTitle title="Store Orders" sub={`${orders.length} total orders`}/>
      <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
        {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
        orders.length===0?<Empty icon={<ShoppingBag size={28}/>} text="No orders yet"/>:
        orders.map((o:any)=>(
          <Card key={o.id||o.ref} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>#{o.ref||o.id?.slice(0,8)}</div>
              <div style={{color:C.dim,fontSize:11}}>{o.email||o.customerEmail} · {o.createdAt?new Date(o.createdAt).toLocaleDateString():''}</div>
            </div>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              <span style={{color:C.gold,fontWeight:700,fontSize:14}}>₦{(o.total||o.amount||0).toLocaleString()}</span>
              <span style={{
                fontSize:10,padding:'2px 8px',borderRadius:10,
                background:`${C.green}22`,color:C.green
              }}>{o.status||'completed'}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── HOTEL REGISTRATIONS PANEL ─────────────────────────────────────────────────
function HotelRegsPanel({api,flash}:{api:any;flash:any}) {
  const [regs,setR]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/hotel-registrations');if(Array.isArray(d))setR(d);else setR(d.registrations||d.data||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  const update=async(id:string,status:string)=>{
    const d=await api(`/api/hotel-registrations/${id}`,{method:'PUT',body:JSON.stringify({status})});
    if(d.success){flash('ok','Updated');load();}else flash('err','Failed');
  };
  return (
    <div>
      <PanelTitle title="Hotel Registrations" sub={`${regs.length} hotel/lodging registrations`}/>
      <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
        {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
        regs.length===0?<Empty icon={<Hotel size={28}/>} text="No hotel registrations"/>:
        regs.map((r:any)=>(
          <Card key={r.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{r.guestName||r.name}</div>
              <div style={{color:C.dim,fontSize:11}}>{r.hotelName||r.hotel} · {r.checkIn} → {r.checkOut} · {r.email}</div>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{
                fontSize:10,padding:'2px 8px',borderRadius:10,
                background: r.status==='confirmed'?`${C.green}22`:`${C.amber}22`,
                color: r.status==='confirmed'?C.green:C.amber
              }}>{r.status||'pending'}</span>
              {r.status!=='confirmed'&&<Btn onClick={()=>update(r.id,'confirmed')} variant="green"><CheckCircle size={12}/></Btn>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── DIASPORA ADMIN PANEL ──────────────────────────────────────────────────────
function DiasporaAdminPanel({api,flash}:{api:any;flash:any}) {
  const [members,setM]=useState<any[]>([]);
  const [pledges,setP]=useState<any[]>([]);
  const [tab,setTab]=useState<'members'|'pledges'>('members');
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);
    try{
      const [md,pd]=await Promise.all([api('/api/admin/diaspora/members'),api('/api/admin/diaspora/pledges')]);
      if(md.success)setM(md.members||[]);
      if(pd.success)setP(pd.pledges||[]);
    }finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  const updatePledge=async(id:string,status:string)=>{
    const d=await api(`/api/admin/diaspora/pledge/${id}`,{method:'PUT',body:JSON.stringify({status})});
    if(d.success){flash('ok','Updated');load();}else flash('err','Failed');
  };
  return (
    <div>
      <PanelTitle title="Diaspora Administration" sub="Manage diaspora registrations and pledges"/>
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        {(['members','pledges'] as const).map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'7px 16px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',
            background: tab===t?C.gold:'transparent',
            color: tab===t?'#070D0A':C.muted,
            border:`1px solid ${tab===t?C.gold:C.border}`
          }}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/></Btn>
      </div>
      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
      tab==='members'?(
        members.length===0?<Empty icon={<Globe size={28}/>} text="No diaspora members"/>:
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {members.map((m:any)=>(
            <Card key={m.id} style={{padding:'10px 14px'}}>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{m.name||m.fullName}</div>
              <div style={{color:C.dim,fontSize:11}}>{m.email} · {m.country} · {m.chapter}</div>
            </Card>
          ))}
        </div>
      ):(
        pledges.length===0?<Empty icon={<Globe size={28}/>} text="No pledges"/>:
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {pledges.map((p:any)=>(
            <Card key={p.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
              <div>
                <div style={{color:C.text,fontSize:13,fontWeight:600}}>{p.name||p.donorName}</div>
                <div style={{color:C.dim,fontSize:11}}>{p.email} · ₦{(p.amount||0).toLocaleString()} · {p.purpose}</div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <span style={{
                  fontSize:10,padding:'2px 8px',borderRadius:10,
                  background: p.status==='fulfilled'?`${C.green}22`:`${C.amber}22`,
                  color: p.status==='fulfilled'?C.green:C.amber
                }}>{p.status||'pending'}</span>
                {p.status!=='fulfilled'&&<Btn onClick={()=>updatePledge(p.id,'fulfilled')} variant="green"><CheckCircle size={12}/></Btn>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TELEGRAM PANEL ────────────────────────────────────────────────────────────
function TelegramPanel({api,flash}:{api:any;flash:any}) {
  const [bindings,setB]=useState<any[]>([]);
  const [email,setEmail]=useState('');
  const [tgId,setTgId]=useState('');
  const [clearance,setClearance]=useState('MEMBER');
  const load=useCallback(async()=>{
    const d=await api('/api/integrations/telegram/bindings');if(d.success)setB(d.bindings||[]);
  },[api]);
  useEffect(()=>{load();},[load]);
  const bind=async()=>{
    if(!email.trim()||!tgId.trim()) return flash('err','Email and Telegram ID required');
    const d=await api('/api/integrations/telegram/bind',{method:'POST',body:JSON.stringify({email,telegramId:tgId,clearanceLevel:clearance})});
    if(d.success){flash('ok','Binding created');setEmail('');setTgId('');load();}else flash('err',d.error||'Failed');
  };
  return (
    <div>
      <PanelTitle title="Telegram Bot Bindings" sub="Link Telegram IDs to platform users for /approve and /reject commands"/>
      <Card style={{marginBottom:20, maxWidth:520}}>
        <div style={{color:C.gold,fontSize:13,fontWeight:700,marginBottom:12}}>Bind New User</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <Input value={email} onChange={setEmail} placeholder="User email address"/>
          <Input value={tgId} onChange={setTgId} placeholder="Telegram Chat ID (numeric)"/>
          <select value={clearance} onChange={e=>setClearance(e.target.value)} style={{
            background:C.card,border:`1px solid ${C.border}`,borderRadius:8,
            padding:'8px 12px',color:C.text,fontSize:13,outline:'none'
          }}>
            {['MEMBER','OFFICER','ADMIN'].map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <Btn onClick={bind}><Link2 size={13}/>Create Binding</Btn>
        </div>
      </Card>
      <Card style={{marginBottom:16,background:`${C.amber}11`,border:`1px solid ${C.amber}33`}}>
        <div style={{color:C.amber,fontSize:12,fontWeight:700,marginBottom:6}}>📱 Bot Command Reference</div>
        <div style={{color:C.muted,fontSize:12,lineHeight:1.6}}>
          <div>Webhook URL: <code style={{color:C.gold}}>/api/integrations/telegram/webhook</code></div>
          <div>Secret header: <code style={{color:C.gold}}>x-telegram-bot-api-secret-token: erue-tg-secret-2026</code></div>
          <div>Approve task: <code style={{color:C.green}}>/approve &lt;task-uuid&gt;</code></div>
          <div>Reject task: <code style={{color:C.red}}>/reject &lt;task-uuid&gt;</code></div>
        </div>
      </Card>
      <div style={{color:C.muted,fontSize:12,marginBottom:10}}>{bindings.length} active binding(s)</div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {bindings.length===0?<Empty icon={<Send size={24}/>} text="No Telegram bindings yet"/>:
        bindings.map((b:any)=>(
          <Card key={b.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px'}}>
            <div>
              <div style={{color:C.text,fontSize:13,fontWeight:600}}>{b.email}</div>
              <div style={{color:C.dim,fontSize:11}}>TG ID: {b.telegramId} · {b.clearanceLevel}</div>
            </div>
            <span style={{fontSize:10,padding:'2px 8px',background:`${C.green}22`,color:C.green,borderRadius:10}}>Active</span>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── WORKFLOW TASKS PANEL ──────────────────────────────────────────────────────
function WorkflowPanel({api,flash}:{api:any;flash:any}) {
  const [tasks,setT]=useState<any[]>([]);
  const [loading,setL]=useState(false);
  const [form,setForm]=useState(false);
  const [title,setTitle]=useState('');
  const [desc,setDesc]=useState('');
  const [assignedTo,setAssignedTo]=useState('');
  const [priority,setPriority]=useState('NORMAL');
  const [filterStatus,setFS]=useState('ALL');

  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/workflow/tasks');setT(d.tasks||d.data||[]);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);

  const create=async()=>{
    if(!title.trim()) return flash('err','Title required');
    const d=await api('/api/workflow/tasks',{method:'POST',body:JSON.stringify({title,description:desc,assignedTo,priority})});
    if(d.success||d.task){flash('ok','Task created');setForm(false);setTitle('');setDesc('');setAssignedTo('');load();}
    else flash('err',d.error||'Failed');
  };
  const update=async(id:string,status:string)=>{
    const d=await api(`/api/workflow/tasks/${id}`,{method:'PUT',body:JSON.stringify({status})});
    if(d.success){flash('ok','Updated');load();}else flash('err','Failed');
  };
  const del=async(id:string)=>{
    if(!confirm('Delete task?')) return;
    const d=await api(`/api/workflow/tasks/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err','Failed');
  };

  const statusColor:Record<string,string>={PENDING:C.amber,APPROVED:C.green,REJECTED:C.red,CANCELLED:C.dim};
  const priorityColor:Record<string,string>={LOW:C.dim,NORMAL:C.blue,HIGH:C.amber,URGENT:C.red};
  const filtered=filterStatus==='ALL'?tasks:tasks.filter((t:any)=>t.status===filterStatus);

  return (
    <div>
      <PanelTitle title="Workflow Tasks" sub={`${tasks.length} tasks · Telegram /approve and /reject enabled`}/>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        {['ALL','PENDING','APPROVED','REJECTED','CANCELLED'].map(s=>(
          <button key={s} onClick={()=>setFS(s)} style={{
            padding:'5px 12px',borderRadius:20,fontSize:11,fontWeight:600,cursor:'pointer',
            background: filterStatus===s?C.gold:'transparent',
            color: filterStatus===s?'#070D0A':C.muted,
            border:`1px solid ${filterStatus===s?C.gold:C.border}`
          }}>{s}</button>
        ))}
        <div style={{flex:1}}/>
        <Btn onClick={load} variant="ghost"><RefreshCw size={13}/></Btn>
        <Btn onClick={()=>setForm(true)}><Plus size={13}/>New Task</Btn>
      </div>

      {form&&(
        <Card style={{marginBottom:16,maxWidth:520}}>
          <div style={{color:C.text,fontWeight:700,marginBottom:12}}>Create Task</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <Input value={title} onChange={setTitle} placeholder="Task title *"/>
            <Textarea value={desc} onChange={setDesc} placeholder="Description (optional)" rows={3}/>
            <Input value={assignedTo} onChange={setAssignedTo} placeholder="Assign to email (optional)"/>
            <select value={priority} onChange={e=>setPriority(e.target.value)} style={{
              background:C.card,border:`1px solid ${C.border}`,borderRadius:8,
              padding:'8px 12px',color:C.text,fontSize:13,outline:'none'
            }}>
              {['LOW','NORMAL','HIGH','URGENT'].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{display:'flex',gap:10,marginTop:12}}>
            <Btn onClick={()=>setForm(false)} variant="ghost">Cancel</Btn>
            <Btn onClick={create}><Save size={13}/>Create Task</Btn>
          </div>
        </Card>
      )}

      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Loading…</div>:
      filtered.length===0?<Empty icon={<ListTodo size={28}/>} text="No tasks"/>:
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {filtered.map((t:any)=>(
          <Card key={t.id}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                  <span style={{color:C.text,fontWeight:600,fontSize:13}}>{t.title}</span>
                  <span style={{
                    fontSize:10,padding:'2px 8px',borderRadius:10,
                    background:`${statusColor[t.status]||C.dim}22`,
                    color:statusColor[t.status]||C.dim
                  }}>{t.status}</span>
                  <span style={{fontSize:10,color:priorityColor[t.priority]||C.dim}}>[{t.priority}]</span>
                </div>
                {t.description&&<div style={{color:C.muted,fontSize:12,marginBottom:4}}>{t.description}</div>}
                <div style={{color:C.dim,fontSize:10}}>
                  ID: <code style={{color:C.gold,fontSize:9}}>{t.id}</code>
                  {t.assignedTo&&<span> · Assigned: {t.assignedTo}</span>}
                </div>
                <div style={{color:C.dim,fontSize:10,marginTop:4}}>
                  Telegram: <code style={{color:C.gold}}>/approve {t.id}</code>
                  {' · '}<code style={{color:C.red}}>/reject {t.id}</code>
                </div>
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0}}>
                {t.status==='PENDING'&&<>
                  <Btn onClick={()=>update(t.id,'APPROVED')} variant="green"><CheckCircle size={12}/>Approve</Btn>
                  <Btn onClick={()=>update(t.id,'REJECTED')} variant="red"><XCircle size={12}/>Reject</Btn>
                </>}
                <Btn onClick={()=>del(t.id)} variant="ghost"><Trash2 size={12}/></Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>}
    </div>
  );
}

// ── DATA HEALTH PANEL ─────────────────────────────────────────────────────────
function DataHealthPanel({api}:{api:any}) {
  const [report,setR]=useState<any>(null);
  const [loading,setL]=useState(false);
  const load=useCallback(async()=>{
    setL(true);try{const d=await api('/api/portal/data-health');setR(d);}finally{setL(false);}
  },[api]);
  useEffect(()=>{load();},[load]);
  return (
    <div>
      <PanelTitle title="Data Health Report" sub="System data integrity and completeness check"/>
      <Btn onClick={load} variant="ghost"><RefreshCw size={13}/>Refresh</Btn>
      {loading?<div style={{textAlign:'center',padding:40,color:C.dim}}>Running health check…</div>:
      report?(
        <Card style={{marginTop:16}}>
          <pre style={{color:C.text,fontSize:11,overflowX:'auto',margin:0,lineHeight:1.6}}>
            {JSON.stringify(report,null,2)}
          </pre>
        </Card>
      ):<Empty icon={<HeartPulse size={28}/>} text="Click Refresh to run health check"/>}
    </div>
  );
}

// ── PURGE PANEL ───────────────────────────────────────────────────────────────
function PurgePanel({api,flash}:{api:any;flash:any}) {
  const [preview,setPreview]=useState<any>(null);
  const [loading,setL]=useState(false);
  const [purging,setPurging]=useState(false);

  const loadPreview=useCallback(async()=>{
    setL(true);try{const d=await api('/api/admin/purge-mock-data/preview');setPreview(d);}finally{setL(false);}
  },[api]);

  const purge=async()=>{
    if(!confirm('⚠️ This will permanently delete all mock/test data. Are you sure?')) return;
    setPurging(true);
    try{
      const d=await api('/api/admin/purge-mock-data',{method:'POST'});
      if(d.success) flash('ok','Mock data purged successfully'); else flash('err',d.error||'Purge failed');
      setPreview(null);
    }finally{setPurging(false);}
  };

  return (
    <div>
      <PanelTitle title="Purge Mock Data" sub="Remove test/demo data from the system"/>
      <Card style={{background:`${C.red}11`,border:`1px solid ${C.red}33`,marginBottom:20}}>
        <div style={{display:'flex',gap:10,alignItems:'center',color:C.red}}>
          <AlertTriangle size={18}/>
          <div>
            <div style={{fontWeight:700,fontSize:13}}>Danger Zone</div>
            <div style={{fontSize:12,opacity:0.8}}>This action permanently deletes mock data. Preview first before purging.</div>
          </div>
        </div>
      </Card>
      <div style={{display:'flex',gap:10,marginBottom:16}}>
        <Btn onClick={loadPreview} variant="ghost" disabled={loading}><Eye size={13}/>{loading?'Loading…':'Preview Affected Data'}</Btn>
        {preview&&<Btn onClick={purge} variant="red" disabled={purging}><Flame size={13}/>{purging?'Purging…':'Execute Purge'}</Btn>}
      </div>
      {preview&&(
        <Card>
          <div style={{color:C.text,fontWeight:700,marginBottom:10}}>Preview</div>
          <pre style={{color:C.muted,fontSize:11,overflowX:'auto',margin:0,lineHeight:1.6}}>
            {JSON.stringify(preview,null,2)}
          </pre>
        </Card>
      )}
    </div>
  );
}


// ── MetaHealthIndicator ───────────────────────────────────────────────────────
function MetaHealthIndicator({api}:{api:any}) {
  const [health,setHealth]=useState<any>(null);
  const [loading,setL]=useState(false);
  const check=useCallback(async()=>{
    setL(true);
    try{ const d=await api('/api/admin/integrations/meta/health'); setHealth(d); }
    finally{ setL(false); }
  },[api]);
  useEffect(()=>{check();},[check]);
  const ok = health?.success && health?.data?.status==='ok';
  return (
    <div style={{
      display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,marginBottom:16,
      background: loading?`${C.border}33`: ok?`${C.green}18`:`${C.amber}18`,
      border:`1px solid ${ok?C.green:C.amber}44`
    }}>
      {loading
        ? <><RefreshCw size={13} color={C.muted} style={{animation:'spin 1s linear infinite'}}/><span style={{color:C.muted,fontSize:12}}>Checking Meta health…</span></>
        : ok
          ? <><CheckCircle size={13} color={C.green}/><span style={{color:C.green,fontSize:12}}>Meta API — Connected</span></>
          : <><AlertTriangle size={13} color={C.amber}/><span style={{color:C.amber,fontSize:12}}>Meta API — {health?.error||'Not configured'}</span></>
      }
      <button onClick={check} style={{marginLeft:'auto',background:'transparent',border:'none',color:C.dim,cursor:'pointer',fontSize:11}}>
        ↺ Recheck
      </button>
    </div>
  );
}


// ── MaintenancePanel ─────────────────────────────────────────────────────────
function MaintenancePanel({api,flash}:{api:any;flash:any}) {
  const [busy,setBusy]=React.useState(false);
  const purge = async (label:string, url:string) => {
    if (!window.confirm(`Permanently delete all ${label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const d = await api(url, {method:'DELETE'});
      flash('ok', `${label} purged — ${d.deleted||0} records removed`);
    } catch(e:any) { flash('err', `Failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  const items = [
    {label:'Test/Draft News Articles', url:'/api/admin/maintenance/purge-draft-news'},
    {label:'Empty Meeting Rooms', url:'/api/admin/maintenance/purge-empty-rooms'},
    {label:'Unpaid Raffle Tickets', url:'/api/admin/maintenance/purge-unpaid-raffle'},
    {label:'Old Audit Logs (>90 days)', url:'/api/admin/maintenance/purge-old-audit'},
    {label:'Old Page Views', url:'/api/admin/maintenance/purge-page-views'},
    {label:'Cancelled Event Registrations', url:'/api/admin/maintenance/purge-cancelled-regs'},
  ];
  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}>
        <h2 style={{color:C.red,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>🧹 Data Maintenance</h2>
        <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Permanently remove test, mock, and expired data. Each action is irreversible.</p>
      </div>
          <div style={{ marginTop:'24px', padding:'20px', background:C.card, borderRadius:'12px', border:'1px solid '+C.gold+'44', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <h4 style={{ color:C.gold, margin:0 }}>Staff Operations Manual</h4>
              <p style={{ color:C.muted, margin:'4px 0 0 0', fontSize:'12px' }}>Complete guide for platform operations and event management.</p>
            </div>
            <button onClick={async () => {
              try {
                const resp = await fetch('/api/admin/manual', { headers:{ Authorization:`Bearer ${localStorage.getItem('erue_auth_token')}` } });
                if (!resp.ok) throw new Error('failed');
                const blob = await resp.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'ERUEFESTSUITE_Manual.html';
                document.body.appendChild(a); a.click(); a.remove();
              } catch { alert('Manual not available. Contact administrator.'); }
            }} style={{ background:C.gold, color:C.bg, border:'none', padding:'10px 20px', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px' }}>
              <Download size={16} /> Download Manual
            </button>
          </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
        {items.map(({label,url},i)=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.red}44`,borderRadius:8,padding:16,display:'flex',flexDirection:'column',gap:10}}>
            <div style={{color:C.text,fontSize:13,fontWeight:600}}>{label}</div>
            <button disabled={busy} onClick={()=>purge(label,url)} style={{
              background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,
              color:C.red,fontSize:12,fontWeight:700,padding:'6px 14px',cursor:'pointer',opacity:busy?0.5:1
            }}>🗑 Purge</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── EventsAdminPanel ──────────────────────────────────────────────────────────
function EventsAdminPanel({api,flash}:{api:any;flash:any}) {
  const [items,setItems]=React.useState<any[]>([]);
  const [form,setForm]=React.useState({title:'',description:'',startDate:'',endDate:'',location:'',type:'general',isPublic:true});
  const [editing,setEditing]=React.useState<string|null>(null);
  const load=React.useCallback(async()=>{
    const d=await api('/api/public/events'); setItems(Array.isArray(d)?d:d.events||[]);
  },[api]);
  React.useEffect(()=>{load();},[load]);
  const save=async()=>{
    const url=editing?`/api/admin/events/${editing}`:'/api/admin/events';
    const method=editing?'PUT':'POST';
    const d=await api(url,{method,body:JSON.stringify(form)});
    if(d.success){flash('ok',editing?'Event updated':'Event created');setEditing(null);setForm({title:'',description:'',startDate:'',endDate:'',location:'',type:'general',isPublic:true});load();}
    else flash('err',d.error||'Failed');
  };
  const del=async(id:string)=>{
    if(!window.confirm('Delete this event?'))return;
    const d=await api(`/api/admin/events/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err',d.error||'Failed');
  };
  const startEdit=(ev:any)=>{setEditing(ev.id);setForm({title:ev.title||'',description:ev.description||'',startDate:ev.startDate?.slice(0,10)||'',endDate:ev.endDate?.slice(0,10)||'',location:ev.location||'',type:ev.type||'general',isPublic:ev.isPublic!==false});};
  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}><h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0}}>🗓 Events Manager</h2></div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <input placeholder='Title' value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12,gridColumn:'1/-1'}}/>
          <input placeholder='Start Date' type='date' value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          <input placeholder='End Date' type='date' value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          <input placeholder='Location' value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}>
            {['general','cultural','sports','religious','social'].map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button onClick={save} style={{background:C.gold,color:'#070D0A',border:'none',borderRadius:6,padding:'8px 18px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
          {editing?'Update Event':'Add Event'}
        </button>
        {editing&&<button onClick={()=>{setEditing(null);setForm({title:'',description:'',startDate:'',endDate:'',location:'',type:'general',isPublic:true});}} style={{marginLeft:8,background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 14px',color:C.muted,fontSize:12,cursor:'pointer'}}>Cancel</button>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:'center',padding:32}}>No events yet.</div>}
        {items.map((ev:any)=>(
          <div key={ev.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
            <div style={{flex:1}}>
              <div style={{color:C.text,fontWeight:600,fontSize:13}}>{ev.title}</div>
              <div style={{color:C.muted,fontSize:11}}>{ev.startDate?.slice(0,10)} • {ev.location||'—'} • {ev.type}</div>
            </div>
            <button onClick={()=>startEdit(ev)} style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Edit</button>
            <button onClick={()=>del(ev.id)} style={{background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ContestantsAdminPanel ─────────────────────────────────────────────────────
function ContestantsAdminPanel({api,flash}:{api:any;flash:any}) {
  const [items,setItems]=React.useState<any[]>([]);
  const [form,setForm]=React.useState({name:'',code:'',bio:'',photoUrl:'',village:'',age:''});
  const [editing,setEditing]=React.useState<string|null>(null);
  const load=React.useCallback(async()=>{const d=await api('/api/admin/contestants');setItems(Array.isArray(d)?d:d.contestants||d.data||[]);},[api]);
  React.useEffect(()=>{load();},[load]);
  const save=async()=>{
    const url=editing?`/api/admin/contestants/${editing}`:'/api/admin/contestants';
    const method=editing?'PUT':'POST';
    const d=await api(url,{method,body:JSON.stringify(form)});
    if(d.success){flash('ok',editing?'Updated':'Created');setEditing(null);setForm({name:'',code:'',bio:'',photoUrl:'',village:'',age:''});load();}
    else flash('err',d.error||'Failed');
  };
  const del=async(id:string)=>{
    if(!window.confirm('Delete contestant?'))return;
    const d=await api(`/api/admin/contestants/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err',d.error||'Failed');
  };
  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}><h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0}}>👑 Contestants Manager</h2></div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          {(['name','code','village','age'] as const).map(k=>(
            <input key={k} placeholder={k.charAt(0).toUpperCase()+k.slice(1)} value={(form as any)[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          ))}
          <input placeholder='Photo URL' value={form.photoUrl} onChange={e=>setForm(p=>({...p,photoUrl:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12,gridColumn:'1/-1'}}/>
        </div>
        <button onClick={save} style={{background:C.gold,color:'#070D0A',border:'none',borderRadius:6,padding:'8px 18px',fontSize:12,fontWeight:700,cursor:'pointer'}}>{editing?'Update':'Add Contestant'}</button>
        {editing&&<button onClick={()=>{setEditing(null);setForm({name:'',code:'',bio:'',photoUrl:'',village:'',age:''}); }} style={{marginLeft:8,background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 14px',color:C.muted,fontSize:12,cursor:'pointer'}}>Cancel</button>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:'center',padding:32}}>No contestants yet.</div>}
        {items.map((c:any)=>(
          <div key={c.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
            {c.photoUrl&&<img src={c.photoUrl} style={{width:40,height:40,borderRadius:'50%',objectFit:'cover'}} alt={c.name}/>}
            <div style={{flex:1}}><div style={{color:C.text,fontWeight:600,fontSize:13}}>{c.name} <span style={{color:C.muted,fontSize:11}}>({c.code})</span></div><div style={{color:C.muted,fontSize:11}}>{c.village} • Votes: {c.votes||0}</div></div>
            <button onClick={()=>{setEditing(c.id);setForm({name:c.name||'',code:c.code||'',bio:c.bio||'',photoUrl:c.photoUrl||'',village:c.village||'',age:String(c.age||'')});}} style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Edit</button>
            <button onClick={()=>del(c.id)} style={{background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SponsorsAdminPanel ────────────────────────────────────────────────────────
function SponsorsAdminPanel({api,flash}:{api:any;flash:any}) {
  const [items,setItems]=React.useState<any[]>([]);
  const [form,setForm]=React.useState({name:'',tier:'bronze',logoUrl:'',websiteUrl:'',description:''});
  const [editing,setEditing]=React.useState<string|null>(null);
  const load=React.useCallback(async()=>{const d=await api('/api/public/sponsors');setItems(Array.isArray(d)?d:d.sponsors||[]);},[api]);
  React.useEffect(()=>{load();},[load]);
  const save=async()=>{
    const url=editing?`/api/admin/sponsors/${editing}`:'/api/admin/sponsors';
    const method=editing?'PUT':'POST';
    const d=await api(url,{method,body:JSON.stringify(form)});
    if(d.success){flash('ok',editing?'Updated':'Created');setEditing(null);setForm({name:'',tier:'bronze',logoUrl:'',websiteUrl:'',description:''});load();}
    else flash('err',d.error||'Failed');
  };
  const del=async(id:string)=>{
    if(!window.confirm('Delete sponsor?'))return;
    const d=await api(`/api/admin/sponsors/${id}`,{method:'DELETE'});
    if(d.success){flash('ok','Deleted');load();}else flash('err',d.error||'Failed');
  };
  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}><h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0}}>🏆 Sponsors Manager</h2></div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <input placeholder='Sponsor Name' value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          <select value={form.tier} onChange={e=>setForm(p=>({...p,tier:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}>
            {['platinum','gold','silver','bronze'].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
          <input placeholder='Logo URL' value={form.logoUrl} onChange={e=>setForm(p=>({...p,logoUrl:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
          <input placeholder='Website URL' value={form.websiteUrl} onChange={e=>setForm(p=>({...p,websiteUrl:e.target.value}))} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:'7px 10px',color:C.text,fontSize:12}}/>
        </div>
        <button onClick={save} style={{background:C.gold,color:'#070D0A',border:'none',borderRadius:6,padding:'8px 18px',fontSize:12,fontWeight:700,cursor:'pointer'}}>{editing?'Update':'Add Sponsor'}</button>
        {editing&&<button onClick={()=>{setEditing(null);setForm({name:'',tier:'bronze',logoUrl:'',websiteUrl:'',description:''});}} style={{marginLeft:8,background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 14px',color:C.muted,fontSize:12,cursor:'pointer'}}>Cancel</button>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:'center',padding:32}}>No sponsors yet.</div>}
        {items.map((s:any)=>(
          <div key={s.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
            {s.logoUrl&&<img src={s.logoUrl} style={{width:40,height:40,borderRadius:6,objectFit:'contain'}} alt={s.name}/>}
            <div style={{flex:1}}><div style={{color:C.text,fontWeight:600,fontSize:13}}>{s.name}</div><div style={{color:C.muted,fontSize:11,textTransform:'capitalize'}}>{s.tier} sponsor</div></div>
            <button onClick={()=>{setEditing(s.id);setForm({name:s.name||'',tier:s.tier||'bronze',logoUrl:s.logoUrl||'',websiteUrl:s.websiteUrl||'',description:s.description||''});}} style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Edit</button>
            <button onClick={()=>del(s.id)} style={{background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── RaffleAdminPanel ────────────────────────────────────────────────────────
function RaffleAdminPanel({api,flash}:{api:any;flash:any}) {
  const C = { bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22', text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444', amber:'#f59e0b', green:'#22c55e' };
  const inp: React.CSSProperties = { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'7px 10px', color:C.text, fontSize:12, outline:'none', boxSizing:'border-box' };
  const TIERS = ['GRAND','MAJOR','MINOR','CONSOLATION'];
  const CATS  = ['Royal Regalia','Historical Artifacts','Masquerade & Music','Festival Moments'];
  const empty = { name:'', tier:'CONSOLATION', description:'', value:'', imageUrl:'', ticketPrice:'0', ticketsAvailable:'0', prizeCount:'1', sortOrder:'0' };
  const [prizes, setPrizes] = React.useState<any[]>([]);
  const [cfg, setCfg]       = React.useState<any>({ ticketPrice:1000, currency:'NGN', isOpen:true });
  const [form, setForm]     = React.useState<any>(empty);
  const [editing, setEditing] = React.useState<string|null>(null);
  const [busy, setBusy]     = React.useState(false);
  const [cfgDirty, setCfgDirty] = React.useState(false);
  const tok = () => localStorage.getItem('erue_auth_token') || '';
  const hdr = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${tok()}` });

  const load = React.useCallback(async () => {
    try {
      const r = await fetch('/api/raffle/admin/summary', { headers: hdr() });
      const d = await r.json();
      if (d.success) {
        const r2 = await fetch('/api/public/raffle-prizes', { headers: hdr() });
        const d2 = await r2.json();
        if (d2.success) { setPrizes(d2.prizes); setCfg(d2.config || cfg); }
      }
    } catch(e) { flash('Failed to load raffle data', false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const fld = (k: string, v: string) => setForm((p: any) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const url  = editing ? `/api/raffle/admin/prizes/${editing}` : '/api/raffle/admin/prizes';
      const meth = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method:meth, headers:hdr(), body:JSON.stringify(form) });
      const d = await r.json();
      if (d.success) { flash(editing ? 'Prize updated' : 'Prize added'); setForm(empty); setEditing(null); load(); }
      else flash(d.error || 'Failed', false);
    } catch(e) { flash('Network error', false); }
    setBusy(false);
  };

  const del = async (id: string) => {
    if (!confirm('Delete this prize?')) return;
    const r = await fetch(`/api/raffle/admin/prizes/${id}`, { method:'DELETE', headers:hdr() });
    const d = await r.json();
    if (d.success) { flash('Deleted'); load(); } else flash(d.error||'Failed', false);
  };

  const startEdit = (p: any) => {
    setEditing(p.id);
    setForm({ name:p.name||'', tier:p.tier||'CONSOLATION', description:p.description||'', value:p.value||'', imageUrl:p.imageUrl||'', ticketPrice:String(p.ticketPrice||0), ticketsAvailable:String(p.ticketsAvailable||0), prizeCount:String(p.prizeCount||1), sortOrder:String(p.sortOrder||0) });
  };

  const saveCfg = async () => {
    const r = await fetch('/api/raffle/config', { method:'PUT', headers:hdr(), body:JSON.stringify(cfg) });
    const d = await r.json();
    if (d.success) { flash('Raffle config saved'); setCfgDirty(false); } else flash(d.error||'Failed', false);
  };

  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}>
        <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>🎟️ Raffle Prize Manager</h2>
        <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Configure prizes with ticket price, quantity pool, and winner count per tier.</p>
      </div>

      {/* Config row */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20,display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <label style={{fontSize:11,color:C.muted}}>Ticket Price (₦)</label>
          <input type="number" value={cfg.ticketPrice} style={{...inp,width:120}}
            onChange={e=>{setCfg((p:any)=>({...p,ticketPrice:parseFloat(e.target.value)||0}));setCfgDirty(true);}}/>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <label style={{fontSize:11,color:C.muted}}>Sales Open?</label>
          <select value={cfg.isOpen?'true':'false'} style={{...inp,width:100}}
            onChange={e=>{setCfg((p:any)=>({...p,isOpen:e.target.value==='true'}));setCfgDirty(true);}}>
            <option value="true">Open</option>
            <option value="false">Closed</option>
          </select>
        </div>
        {cfgDirty && <button onClick={saveCfg} style={{background:C.gold,color:C.bg,border:'none',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:700,cursor:'pointer'}}>Save Config</button>}
      </div>

      {/* Form */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20}}>
        <h3 style={{color:C.gold,fontSize:14,fontWeight:700,margin:'0 0 12px'}}>{editing ? '✏️ Edit Prize' : '➕ New Prize'}</h3>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Prize Name *</label>
            <input value={form.name} style={inp} placeholder="e.g. Grand Prize — Toyota Camry" onChange={e=>fld('name',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Tier</label>
            <select value={form.tier} style={inp} onChange={e=>fld('tier',e.target.value)}>
              {TIERS.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Prize Value (description, e.g. "₦500,000")</label>
            <input value={form.value} style={inp} placeholder="e.g. ₦500,000 cash" onChange={e=>fld('value',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Ticket Price for this Prize (₦)</label>
            <input type="number" value={form.ticketPrice} style={inp} placeholder="0" onChange={e=>fld('ticketPrice',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Tickets Available (pool size)</label>
            <input type="number" value={form.ticketsAvailable} style={inp} placeholder="e.g. 1000" onChange={e=>fld('ticketsAvailable',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Number of Winners (e.g. 50 consolation prizes)</label>
            <input type="number" value={form.prizeCount} style={inp} placeholder="e.g. 1" onChange={e=>fld('prizeCount',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Image URL (optional)</label>
            <input value={form.imageUrl} style={inp} placeholder="https://..." onChange={e=>fld('imageUrl',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Display Order</label>
            <input type="number" value={form.sortOrder} style={inp} placeholder="0" onChange={e=>fld('sortOrder',e.target.value)}/>
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:10}}>
          <label style={{fontSize:11,color:C.muted}}>Description</label>
          <textarea value={form.description} style={{...inp,minHeight:60,resize:'vertical'}} placeholder="Prize details..." onChange={e=>fld('description',e.target.value)}/>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button disabled={busy||!form.name} onClick={save}
            style={{background:C.gold,color:C.bg,border:'none',borderRadius:6,padding:'8px 18px',fontSize:12,fontWeight:700,cursor:'pointer',opacity:busy?0.6:1}}>
            {editing ? 'Update Prize' : 'Add Prize'}
          </button>
          {editing && <button onClick={()=>{setEditing(null);setForm(empty);}}
            style={{background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 14px',color:C.muted,fontSize:12,cursor:'pointer'}}>
            Cancel
          </button>}
        </div>
      </div>

      {/* Prize list */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {prizes.length===0 && <div style={{color:C.muted,fontSize:13,textAlign:'center',padding:32}}>No prizes configured yet.</div>}
        {prizes.map((p:any) => (
          <div key={p.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'flex-start',gap:12}}>
            {p.imageUrl && <img src={p.imageUrl} style={{width:48,height:48,borderRadius:6,objectFit:'cover',flexShrink:0}} alt={p.name}/>}
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{color:C.gold,fontWeight:700,fontSize:13}}>{p.name}</span>
                <span style={{background:`${C.amber}22`,color:C.amber,border:`1px solid ${C.amber}44`,borderRadius:4,fontSize:10,padding:'1px 7px',fontWeight:700}}>{p.tier}</span>
                {p.won && <span style={{background:`${C.green}22`,color:C.green,border:`1px solid ${C.green}44`,borderRadius:4,fontSize:10,padding:'1px 7px',fontWeight:700}}>✅ DRAWN</span>}
              </div>
              <div style={{display:'flex',gap:16,fontSize:11,color:C.muted,flexWrap:'wrap'}}>
                <span>💰 Value: <strong style={{color:C.text}}>{p.value||'—'}</strong></span>
                <span>🎟️ Ticket ₦: <strong style={{color:C.text}}>{p.ticketPrice||0}</strong></span>
                <span>📦 Pool: <strong style={{color:C.text}}>{p.ticketsAvailable||0}</strong> tickets</span>
                <span>🏆 Winners: <strong style={{color:C.text}}>{p.prizeCount||1}</strong></span>
                <span>📋 Order: {p.sortOrder}</span>
              </div>
              {p.description && <p style={{color:C.muted,fontSize:11,margin:'4px 0 0',lineHeight:1.5}}>{p.description}</p>}
              {p.won && <p style={{color:C.green,fontSize:11,margin:'4px 0 0'}}>Winner: {p.winnerName} — Ticket #{p.winnerTicket}</p>}
            </div>
            <div style={{display:'flex',gap:6,flexShrink:0}}>
              <button onClick={()=>startEdit(p)} style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Edit</button>
              <button onClick={()=>del(p.id)} style={{background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── HeritageAdminPanel ───────────────────────────────────────────────────────
function HeritageAdminPanel({api,flash}:{api:any;flash:any}) {
  const C = { bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22', text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', gold:'#C9A84C', red:'#ef4444', green:'#22c55e' };
  const inp: React.CSSProperties = { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'7px 10px', color:C.text, fontSize:12, outline:'none', boxSizing:'border-box' };
  const CATS = ['Royal Regalia','Historical Artifacts','Masquerade & Music','Festival Moments'];
  const empty = { title:'', category:'Royal Regalia', imageUrl:'', fallbackEmoji:'🏛️', yearEst:'', communityProvenance:'', shortTooltip:'', fullDescription:'', sortOrder:'0', isActive:true };
  const [items, setItems]   = React.useState<any[]>([]);
  const [form, setForm]     = React.useState<any>(empty);
  const [editing, setEditing] = React.useState<string|null>(null);
  const [busy, setBusy]     = React.useState(false);
  const tok = () => localStorage.getItem('erue_auth_token') || '';
  const hdr = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${tok()}` });

  const load = React.useCallback(async () => {
    try {
      const r = await fetch('/api/admin/heritage', { headers: hdr() });
      const d = await r.json();
      if (d.success) setItems(d.items);
    } catch(e) { flash('Failed to load heritage items', false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const fld = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const url  = editing ? `/api/admin/heritage/${editing}` : '/api/admin/heritage';
      const meth = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method:meth, headers:hdr(), body:JSON.stringify(form) });
      const d = await r.json();
      if (d.success) { flash(editing ? 'Updated' : 'Added'); setForm(empty); setEditing(null); load(); }
      else flash(d.error||'Failed', false);
    } catch(e) { flash('Network error', false); }
    setBusy(false);
  };

  const del = async (id: string) => {
    if (!confirm('Delete this heritage item?')) return;
    const r = await fetch(`/api/admin/heritage/${id}`, { method:'DELETE', headers:hdr() });
    const d = await r.json();
    if (d.success) { flash('Deleted'); load(); } else flash(d.error||'Failed', false);
  };

  const startEdit = (item: any) => {
    setEditing(item.id);
    setForm({ title:item.title||'', category:item.category||'Royal Regalia', imageUrl:item.imageUrl||'', fallbackEmoji:item.fallbackEmoji||'🏛️', yearEst:item.yearEst||'', communityProvenance:item.communityProvenance||'', shortTooltip:item.shortTooltip||'', fullDescription:item.fullDescription||'', sortOrder:String(item.sortOrder||0), isActive:item.isActive!==false });
  };

  return (
    <div style={{color:C.text}}>
      <div style={{marginBottom:20}}>
        <h2 style={{color:C.gold,fontWeight:700,fontSize:18,margin:0,fontFamily:'Cinzel,serif'}}>🏛️ Cultural Heritage Gallery</h2>
        <p style={{color:C.muted,fontSize:13,margin:'4px 0 0'}}>Manage heritage artifacts and cultural items displayed in the public gallery. All images are real URLs you supply — no mock data.</p>
      </div>

      {/* Form */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:20}}>
        <h3 style={{color:C.gold,fontSize:14,fontWeight:700,margin:'0 0 12px'}}>{editing ? '✏️ Edit Item' : '➕ New Heritage Item'}</h3>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Title *</label>
            <input value={form.title} style={inp} placeholder="e.g. Ogieneni Sacred Coral Crown" onChange={e=>fld('title',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Category *</label>
            <select value={form.category} style={inp} onChange={e=>fld('category',e.target.value)}>
              {CATS.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Image URL (real photo)</label>
            <input value={form.imageUrl} style={inp} placeholder="https://your-cdn.com/photo.jpg" onChange={e=>fld('imageUrl',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Fallback Emoji (shown if no image)</label>
            <input value={form.fallbackEmoji} style={inp} placeholder="🏛️" onChange={e=>fld('fallbackEmoji',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Year / Era (e.g. "c. 1820")</label>
            <input value={form.yearEst} style={inp} placeholder="c. 1820" onChange={e=>fld('yearEst',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Community Provenance</label>
            <input value={form.communityProvenance} style={inp} placeholder="e.g. Jattu Palace Vaults" onChange={e=>fld('communityProvenance',e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1',display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Short Tooltip (shown on hover)</label>
            <input value={form.shortTooltip} style={inp} placeholder="One-line description for hover card" onChange={e=>fld('shortTooltip',e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1',display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Full Description * (shown in lightbox)</label>
            <textarea value={form.fullDescription} style={{...inp,minHeight:80,resize:'vertical'}} placeholder="Full historical provenance and cultural significance..." onChange={e=>fld('fullDescription',e.target.value)}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:C.muted}}>Display Order</label>
            <input type="number" value={form.sortOrder} style={inp} onChange={e=>fld('sortOrder',e.target.value)}/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,paddingTop:20}}>
            <input type="checkbox" checked={form.isActive} onChange={e=>fld('isActive',e.target.checked)} id="hi-active"/>
            <label htmlFor="hi-active" style={{fontSize:12,color:C.muted,cursor:'pointer'}}>Active (visible on public site)</label>
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button disabled={busy||!form.title||!form.fullDescription} onClick={save}
            style={{background:C.gold,color:C.bg,border:'none',borderRadius:6,padding:'8px 18px',fontSize:12,fontWeight:700,cursor:'pointer',opacity:busy?0.6:1}}>
            {editing ? 'Update Item' : 'Add Item'}
          </button>
          {editing && <button onClick={()=>{setEditing(null);setForm(empty);}}
            style={{background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 14px',color:C.muted,fontSize:12,cursor:'pointer'}}>
            Cancel
          </button>}
        </div>
      </div>

      {/* Item list */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.length===0 && <div style={{color:C.muted,fontSize:13,textAlign:'center',padding:32}}>No heritage items yet. Add your first real artifact above.</div>}
        {items.map((item:any) => (
          <div key={item.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'flex-start',gap:12}}>
            <div style={{width:48,height:48,borderRadius:6,background:'#0E1812',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
              {item.imageUrl ? <img src={item.imageUrl} style={{width:'100%',height:'100%',objectFit:'cover'}} alt={item.title}/> : <span style={{fontSize:22}}>{item.fallbackEmoji||'🏛️'}</span>}
            </div>
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{color:C.gold,fontWeight:700,fontSize:13}}>{item.title}</span>
                <span style={{background:`rgba(201,168,76,0.15)`,color:C.gold,border:`1px solid rgba(201,168,76,0.3)`,borderRadius:4,fontSize:10,padding:'1px 7px'}}>{item.category}</span>
                {!item.isActive && <span style={{background:`${C.red}22`,color:C.red,borderRadius:4,fontSize:10,padding:'1px 7px'}}>Hidden</span>}
              </div>
              <div style={{fontSize:11,color:C.muted}}>{item.yearEst&&`${item.yearEst} · `}{item.communityProvenance}</div>
              {item.shortTooltip&&<div style={{fontSize:11,color:C.muted,marginTop:2,fontStyle:'italic'}}>{item.shortTooltip}</div>}
            </div>
            <div style={{display:'flex',gap:6,flexShrink:0}}>
              <button onClick={()=>startEdit(item)} style={{background:`${C.gold}22`,border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Edit</button>
              <button onClick={()=>del(item.id)} style={{background:`${C.red}22`,border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,padding:'5px 10px',cursor:'pointer'}}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
